/**
 * Membership 5787 — pricing display logic.
 *
 * Tiers are fetched from GET /api/membership/tiers as
 * [{ id, name, monthlyPrice, annualPrice, isFree, notes }, ...]. Nothing
 * here hardcodes a dollar figure: every price shown on the page is read
 * off the tier object the backend returned, keyed by the active billing
 * cycle. This is also the fix for the known bug on the old Membership
 * page, where the top tier's price was hardcoded identically into both
 * the "monthly" and "one-time" option lists (src/utils/constant.js) —
 * toggling the frequency never changed that tile. Reading monthlyPrice
 * and annualPrice as two independent fields makes that class of bug
 * impossible: there is only one field per cycle to draw from.
 */
import { computeSeatCost, getSeatRateConfig } from './seatPricing';

// Which field of a tier to read for a given billing cycle. Unrecognized
// cycles fall back to annual rather than throwing, since annual is the
// one-time/default commitment amount.
export const getTierPrice = (tier, billingCycle) => {
  if (!tier) return null;
  if (tier.isFree) return 0;
  return billingCycle === 'monthly' ? tier.monthlyPrice : tier.annualPrice;
};

export const isTierFree = (tier) => Boolean(tier?.isFree);

export const formatCurrency = (amount) => {
  const n = Number(amount);
  if (amount === null || amount === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

// What a monthly pledge adds up to over the required 12 payments — shown
// next to the monthly price so members see the yearly commitment they're
// making, not just the smaller-looking monthly number.
export const getMonthlyCommitmentTotal = (tier) => {
  if (!tier || tier.isFree) return null;
  if (tier.monthlyPrice === null || tier.monthlyPrice === undefined) return null;
  const monthly = Number(tier.monthlyPrice);
  if (!Number.isFinite(monthly)) return null;
  return Math.round(monthly * 12 * 100) / 100;
};

export const getBillingCycleSuffix = (billingCycle) =>
  billingCycle === 'monthly' ? '/month' : 'one-time';

// Client decision (locked wireframe): Family carries "MOST POPULAR",
// First-Year Couples carries "NEW". Matched by id first (the stable key
// the backend contract gives us) and falls back to name text so the
// badge still shows up if the backend ever renames a tier without
// changing its id.
export const getTierBadge = (tier) => {
  if (!tier) return null;
  const id = (tier.id || '').toLowerCase();
  const name = (tier.name || '').toLowerCase();

  if (id === 'family' || name === 'family') return 'MOST POPULAR';
  if (id === 'first-year-couples' || name.includes('first-year') || name.includes('first year')) {
    return 'NEW';
  }
  return null;
};

// Label for the Stripe checkout button. The guest seat flow reuses the
// member flow's MembershipCheckoutForm, whose label was hardcoded to
// 'Confirm membership' — so a guest buying a single $360 seat, on a page
// that had just told them no seats are included WITHOUT a membership, was
// asked to "Confirm membership" to finish. The charge itself was always a
// one-time PaymentIntent (kbt-backend handleGuestSeatEnrollRequest never
// creates a subscription); only the wording was wrong.
//
// Defaults to the member label so any caller that passes nothing — which
// is every member call site — is unaffected.
export const getCheckoutSubmitLabel = ({ isGuest = false, amount = null } = {}) => {
  if (!isGuest || amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return 'Confirm membership';
  }
  return `Pay ${formatCurrency(amount)}`;
};

// The Review & Confirm step (wireframe section 7) needs a real line-item
// tally, not just "you picked Family, monthly" — the wireframe's own
// example is exactly "Family Membership — $126/month × 12 (annual
// pledge) + $100 one-time (1 extra seat). First payment today." Seat
// charges are always one-time even on a monthly membership, so the total
// keeps the recurring membership amount and the one-time seat amount as
// separate addends ("$126/mo + $600") rather than merging them into one
// number that would misrepresent what's actually being charged today vs.
// pledged for the next 11 months.
export const getEnrollmentSummary = ({ tier, billingCycle, seats, seatPricing }) => {
  const { isMemberRate, includedSeats, extraSeatPrice } = getSeatRateConfig(tier, seatPricing);
  const { extraSeats, extraSeatCost } = computeSeatCost({
    additionalSeats: seats?.additionalSeatPeople?.length ?? 0,
    includedSeats,
    isMemberRate,
    extraSeatPrice,
  });

  // No tier picked at all: the tally bar still needs a real total once a
  // guest has added paid seats (KBT feedback — the bar showed a flat "$0"
  // for the guest-only path even after seats were priced). Only the true
  // nothing-selected-yet state falls through to the bar's own placeholder.
  if (!tier) {
    if (extraSeats === 0) return null;
    return {
      membershipLine: 'No membership — guest seats only',
      seatsLine: `${extraSeats} guest seat${extraSeats === 1 ? '' : 's'} — ${formatCurrency(extraSeatCost)} one-time`,
      total: `${formatCurrency(extraSeatCost)} one-time`,
      // A guest seat purchase is ONE charge. "First payment today."
      // (the member wording) implies a schedule that does not exist, and
      // this line sits directly above the Stripe card form — exactly
      // where someone who chose not to join is deciding whether they are
      // about to be signed up for something recurring.
      dueTodayNote: 'One-time payment — no membership, no recurring charges.',
      extraSeats,
      extraSeatCost,
    };
  }

  const price = getTierPrice(tier, billingCycle);
  const free = isTierFree(tier);

  const membershipLine = free
    ? `${tier.name} Membership — FREE`
    : `${tier.name} Membership — ${formatCurrency(price)}${
        billingCycle === 'monthly' ? '/mo × 12' : ' once'
      }`;

  const seatsLine =
    extraSeats > 0
      ? `+ ${extraSeats} extra seat${extraSeats === 1 ? '' : 's'} — ${formatCurrency(extraSeatCost)} one-time`
      : null;

  const totalParts = [];
  if (!free) totalParts.push(billingCycle === 'monthly' ? `${formatCurrency(price)}/mo` : formatCurrency(price));
  if (extraSeatCost > 0) totalParts.push(formatCurrency(extraSeatCost));

  let total;
  if (totalParts.length === 0) {
    total = 'FREE';
  } else if (totalParts.length === 1 && free) {
    total = `${totalParts[0]} one-time`;
  } else {
    total = totalParts.join(' + ');
  }

  return {
    membershipLine,
    seatsLine,
    total,
    dueTodayNote: 'First payment today.',
    // Exposed so the page can decide whether a real charge is coming
    // (gates the payment step for a free tier with extra seats — see
    // enrollmentFlow.js#getStepsForTier).
    extraSeats,
    extraSeatCost,
  };
};
